import { CanMatchFn, Route, Router } from '@angular/router';
import { inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
// By file, not through the `libs/core` barrel — see the note in app.ts. The
// route table is eager, so anything it reaches is in the initial bundle.
import { AuthService } from '../../../../libs/core/auth/auth.service';
import { CrmAuthSessionService } from './pages/auth/services/crm-auth-session.service';
import { CrmOnboardingAccessService } from './pages/auth/services/crm-onboarding-access.service';
import {
  matchCpdModuleRoute,
  matchCpdMyRoute,
  matchCpdTeamRoute,
} from './pages/cpd/cpd-permission.guard';
import { matchMyCommissionsRoute } from './pages/my-commissions/my-commissions-permission.guard';
import { isAppRootPath, isKnownAppUrl } from './core/app-route-paths';

const loadCrmPlaceholderPage = () =>
  import('./pages/crm-placeholder-page/crm-placeholder-page.component').then(
    (m) => m.CrmPlaceholderPageComponent,
  );

const authPaths = new Set(['login', 'verify', 'forgot-password']);

const toTenantPrefix = (companyName: string): string =>
  String(companyName || '')
    .trim()
    .replace(/\s+/g, '')
    .toLowerCase();

const isNonTenantRootPath = (pathSegment: string): boolean => isAppRootPath(pathSegment);

/**
 * `/acme/cases/12` -> `/cases/12`, `/cases/12` -> `/cases/12`.
 *
 * The tenant slug is cosmetic — `App.applyTenantPrefixToUrl` stamps it onto the
 * address bar after navigation — so it has to come off before a path can be
 * checked against the route table.
 */
const stripTenantPrefix = (url: string): string => {
  const normalized = `/${String(url || '').replace(/^\/+/, '')}`;
  const suffixIndex = normalized.search(/[?#]/);
  const path = suffixIndex === -1 ? normalized : normalized.slice(0, suffixIndex);
  const suffix = suffixIndex === -1 ? '' : normalized.slice(suffixIndex);

  const segments = path.split('/').filter(Boolean);
  if (segments.length < 2 || isAppRootPath(segments[0])) {
    return normalized;
  }

  return `/${segments.slice(1).join('/')}${suffix}`;
};

const matchAuthLayoutRoute: CanMatchFn = (_route, segments) => {
  const platformId = inject(PLATFORM_ID);
  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  const authSessionService = inject(CrmAuthSessionService);
  const onboardingAccessService = inject(CrmOnboardingAccessService);
  const router = inject(Router);

  if (segments.length === 0) {
    return false;
  }

  const firstSegment = segments[0]?.path ?? '';
  if (!authPaths.has(firstSegment)) {
    return false;
  }

  if (authSessionService.isAuthenticated(false)) {
    const windowPath = typeof window !== 'undefined' ? window.location.pathname : '';
    // Only restore a path the router can actually resolve. Restoring an unknown
    // path sends it back to the `**` route, which redirects here again — an
    // infinite loop that leaves the app stuck on the loading shell.
    const candidateTarget = stripTenantPrefix(windowPath);
    const restoreTarget = authSessionService.isProtectedRoute(candidateTarget)
      ? candidateTarget
      : authSessionService.getLastProtectedRoute();

    const onboardingState = onboardingAccessService.getStateSnapshot();
    if (!onboardingState) {
      return router.parseUrl(restoreTarget);
    }

    const nextRoute = onboardingAccessService.resolveNextRoute();
    if (nextRoute === '/dashboard' || nextRoute === '/login') {
      return router.parseUrl(restoreTarget);
    }
    return router.parseUrl(nextRoute);
  }

  return true;
};

const matchAuthenticatedRoute: CanMatchFn = (_route, segments) => {
  const platformId = inject(PLATFORM_ID);
  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  const authService = inject(AuthService);
  const authSessionService = inject(CrmAuthSessionService);
  const router = inject(Router);

  authService.session(); // mirror matchOperationalShellRoute: resolve session signal to ensure hydration

  if (!authSessionService.isAuthenticated(false)) {
    return router.parseUrl('/login');
  }

  const requestedPath = `/${segments
    .map((s) => s.path)
    .filter(Boolean)
    .join('/')}`;
  const search = typeof window !== 'undefined' ? window.location.search : '';
  authSessionService.setLastProtectedRoute(`${requestedPath}${search}`);

  return true;
};

const matchAuthenticatedOnboardingRoute: CanMatchFn = (_route, segments) => {
  const platformId = inject(PLATFORM_ID);
  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  const authSessionService = inject(CrmAuthSessionService);
  const router = inject(Router);
  const onboardingAccessService = inject(CrmOnboardingAccessService);

  if (!authSessionService.isAuthenticated(false)) {
    return router.parseUrl('/login');
  }

  const resolvedRoute = onboardingAccessService.resolveNextRoute();
  const requestedRoute = `/${segments
    .map((segment) => segment.path)
    .filter(Boolean)
    .join('/')}`;
  const resolvedBasePath = resolvedRoute.split('?')[0];
  const requestedBasePath = requestedRoute.split('?')[0];

  return resolvedBasePath === requestedBasePath ? true : router.parseUrl(resolvedRoute);
};

const matchOperationalShellRoute: CanMatchFn = (_route, segments) => {
  const platformId = inject(PLATFORM_ID);
  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  const authSessionService = inject(CrmAuthSessionService);
  const router = inject(Router);
  const onboardingAccessService = inject(CrmOnboardingAccessService);
  const authService = inject(AuthService);

  const expectedTenantPrefix = toTenantPrefix(authService.session().company);
  const firstSegment = String(segments[0]?.path || '')
    .trim()
    .toLowerCase();

  if (
    segments.length > 1 &&
    ((expectedTenantPrefix && firstSegment === expectedTenantPrefix) ||
      (!expectedTenantPrefix && !isNonTenantRootPath(firstSegment)))
  ) {
    const remainingPath = segments
      .slice(1)
      .map((segment) => segment.path)
      .filter(Boolean)
      .join('/');

    if (remainingPath) {
      const search = typeof window !== 'undefined' ? window.location.search : '';
      return router.parseUrl(`/${remainingPath}${search}`);
    }
  }

  if (!authSessionService.isAuthenticated(false)) {
    return router.parseUrl('/login');
  }

  const requestedPath = `/${segments
    .map((s) => s.path)
    .filter(Boolean)
    .join('/')}`;

  const onboardingState = onboardingAccessService.getStateSnapshot();
  if (!onboardingState) {
    authSessionService.setLastProtectedRoute(requestedPath);
    return true;
  }

  if (onboardingAccessService.isOperational()) {
    if (onboardingAccessService.isUserOnboardingActive()) {
      return router.parseUrl('/user-onboarding');
    }
    if (onboardingAccessService.isUserUnderReview()) {
      return router.parseUrl('/user-application-under-review');
    }
    authSessionService.setLastProtectedRoute(requestedPath);
    return true;
  }

  return router.parseUrl(onboardingAccessService.resolveNextRoute());
};

const matchUnknownRoute: CanMatchFn = () => {
  const platformId = inject(PLATFORM_ID);
  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  const authSessionService = inject(CrmAuthSessionService);
  const router = inject(Router);

  // Never send an authenticated user to /login from here. matchAuthLayoutRoute
  // restores authenticated users to where they came from, which is this URL, so
  // the two routes would redirect at each other forever and the app would hang
  // on the loading shell. Render 404 and let them navigate out.
  if (authSessionService.isAuthenticated(false)) {
    return true;
  }

  return router.parseUrl('/login');
};

const matchTenantPrefixedAliasRoute: CanMatchFn = (_route, segments) => {
  if (segments.length === 0) {
    return false;
  }

  const router = inject(Router);

  const requestedTenantPrefix = String(segments[0]?.path || '')
    .trim()
    .toLowerCase();

  // Let standard routes continue normally (e.g. /configurations/*, /cpd/*, /dashboard).
  if (isNonTenantRootPath(requestedTenantPrefix)) {
    return false;
  }

  const search = typeof window !== 'undefined' ? window.location.search : '';

  const remainingPath = segments
    .slice(1)
    .map((segment) => segment.path)
    .filter(Boolean)
    .join('/');

  // Bare tenant root (e.g. /acme) — the first segment is the company slug with
  // nothing after it. Treat it like any tenant-prefixed alias and send it to the
  // dashboard; the shell guard still enforces auth/onboarding from there.
  if (!remainingPath) {
    return router.parseUrl(`/dashboard${search}`);
  }

  return router.parseUrl(`/${remainingPath}${search}`);
};

export const appRoutes: Route[] = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'login',
  },
  {
    path: ':tenantPrefix',
    canMatch: [matchTenantPrefixedAliasRoute],
    children: [{ path: '**', redirectTo: '/' }],
  },
  {
    path: '',
    canMatch: [matchAuthLayoutRoute],
    loadComponent: () =>
      import('./layouts/auth-layout/auth-layout.component').then((m) => m.AuthLayoutComponent),
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('./pages/auth/login/login.component').then((m) => m.LoginComponent),
      },
      {
        path: 'verify',
        loadComponent: () =>
          import('./pages/auth/verify/verify.component').then((m) => m.VerifyComponent),
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('./pages/auth/forgot-password/forgot-password.component').then(
            (m) => m.ForgotPasswordComponent,
          ),
      },
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'login',
      },
    ],
  },
  {
    path: 'set-password',
    loadComponent: () =>
      import('./pages/auth/set-password/set-password.component').then(
        (m) => m.SetPasswordComponent,
      ),
  },
  {
    path: 'reset-password',
    loadComponent: () =>
      import('./pages/auth/reset-password/reset-password.component').then(
        (m) => m.ResetPasswordComponent,
      ),
  },
  {
    path: 'error/:code',
    loadComponent: () =>
      import('../../../../libs/core/shared/ui/error-reusable/error-reusable').then(
        (m) => m.ErrorReusableComponent,
      ),
  },
  {
    path: 'manual-tenant',
    canMatch: [matchAuthenticatedOnboardingRoute],
    loadComponent: () =>
      import('./tenant/invite-tenant/invite-tenant.component').then((m) => m.InviteTenantComponent),
  },
  {
    path: 'user-onboarding',
    canMatch: [matchAuthenticatedOnboardingRoute],
    loadComponent: () =>
      import('./user-onboarding/user-onboarding.component').then((m) => m.UserOnboardingComponent),
  },
  {
    path: 'user-application-under-review',
    canMatch: [matchAuthenticatedOnboardingRoute],
    loadComponent: () =>
      import(
        './user-onboarding/user-application-under-review/user-application-under-review.component'
      ).then((m) => m.UserApplicationUnderReviewComponent),
  },
  {
    path: 'user-view-application/:id',
    canMatch: [matchAuthenticatedRoute],
    loadChildren: () =>
      import('./user-view-application/user-view-application.routes').then(
        (m) => m.userViewApplicationRoutes,
      ),
  },
  {
    path: 'in-review-thank-you',
    canMatch: [matchAuthenticatedOnboardingRoute],
    loadComponent: () =>
      import('./pages/in-review-thank-you/in-review-thank-you.component').then(
        (m) => m.InReviewThankYouComponent,
      ),
  },
  {
    path: 'application-under-review',
    canMatch: [matchAuthenticatedOnboardingRoute],
    loadComponent: () =>
      import(
        './tenant/invite-tenant/components/application-under-review/application-under-review'
      ).then((m) => m.ApplicationUnderReviewComponent),
  },
  {
    path: 'create-hq',
    canMatch: [matchAuthenticatedOnboardingRoute],
    loadComponent: () =>
      import('./tenant/invite-tenant/components/create-hq/create-hq.component').then(
        (m) => m.CreateHqComponent,
      ),
  },
  {
    path: '',
    canMatch: [matchOperationalShellRoute],
    loadComponent: () =>
      import('./layouts/crm-shell/crm-shell.component').then((m) => m.CrmShellComponent),
    children: [
      {
        path: 'profile',
        loadComponent: () =>
          import('./pages/profile/my-profile/my-profile').then((m) => m.MyProfile),
      },
      {
        path: 'branch',
        loadComponent: () => import('./pages/profile/my-branch/my-branch').then((m) => m.MyBranch),
      },
      {
        path: 'company-settings',
        data: {
          title: 'Company Settings',
          description: 'Review subscription, branding, and company identity settings.',
        },
        loadComponent: () =>
          import('./pages/company-settings/company-settings.component').then(
            (m) => m.CompanySettingsComponent,
          ),
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./pages/dashboard/dashboard.component').then((m) => m.DashboardComponent),
      },
      {
        path: 'employee-interview-form',
        loadComponent: () =>
          import(
            './pages/user-management/employee-interview-form/employee-interview-form.component'
          ).then((m) => m.EmployeeInterviewFormComponent),
      },

      {
        path: 'introducer-interview-form',
        loadComponent: () =>
          import(
            './pages/user-management/introducer-interview-form/introducer-interview-form'
          ).then((m) => m.IntroducerInterviewForm),
      },
      {
        path: 'registered-individual-interview-form',
        loadComponent: () =>
          import(
            './pages/user-management/registered-individual-interview-form/registered-individual-interview-form'
          ).then((m) => m.RegisteredIndividualInterviewForm),
      },
      {
        path: 'clients',
        data: {
          title: 'Clients',
          description: 'Track and manage all lead activity from one place.',
        },
        loadChildren: () => import('./pages/clients/clients.routes').then((m) => m.clientsRoutes),
      },
      {
        path: 'customers',
        data: {
          title: 'Customers',
          description: 'Access customer records and status updates quickly.',
        },
        loadComponent: loadCrmPlaceholderPage,
      },
      {
        path: 'cases',
        data: {
          title: 'Cases',
          description: 'Monitor and process open cases across the CRM workflow.',
        },
        loadChildren: () => import('./pages/cases/cases.routes').then((m) => m.casesRoutes),
      },
      {
        path: 'quick-sourcing',
        pathMatch: 'full',
        redirectTo: 'product-catalog',
      },
      {
        path: 'product-catalog',
        data: {
          title: 'Product Catalog',
          description: 'Browse and search the catalog of protection and mortgage products.',
        },
        loadChildren: () =>
          import('./pages/product-catalog/product-catalog.routes').then(
            (m) => m.productCatalogRoutes,
          ),
      },
      {
        path: 'sourcing',
        data: {
          title: 'Quick Sourcing',
          description: 'Source protection and mortgage products from one place.',
        },
        loadComponent: () =>
          import('./pages/quick-sourcing-tool/quick-sourcing-tool').then(
            (m) => m.QuickSourcingTool,
          ),
      },
      {
        path: 'user-management',
        data: {
          title: 'User Management',
          description: 'Manage users, access, and operational roles.',
        },
        loadChildren: () =>
          import('./pages/user-management/user-management.routes').then(
            (m) => m.userManagementRoutes,
          ),
      },
      {
        path: 'branch-management',
        data: {
          breadcrumb: 'Branch Management',
        },
        loadChildren: () => import('./pages/branch/branch.routes').then((m) => m.branchRoutes),
      },
      {
        path: 'ar-management',
        data: {
          title: 'AR Management',
          description: 'Review and manage account receivable processes.',
        },
        loadChildren: () =>
          import('./pages/ar-management/ar-management.routes').then((m) => m.arManagementRoutes),
      },

      {
        path: 'commission-management',
        data: {
          title: 'Commission Management',
          description: 'Handle commission setup, review, and approvals.',
        },
        loadComponent: () =>
          import('./pages/commission-management/commission-management').then(
            (m) => m.CommissionManagement,
          ),
      },
      {
        path: 'commission-management/statement/:statementId',
        data: {
          title: 'Commission Statement',
          description: 'Review a paid-out commission statement.',
        },
        // The page picks its endpoint from the `product` on the link that opened
        // it, so nothing is provided here.
        loadComponent: () =>
          import('./pages/commission-management/statement-view/statement-view').then(
            (m) => m.StatementView,
          ),
      },
      {
        path: 'cpd/team-cpd/:userId/record/:recordId',
        canMatch: [matchCpdModuleRoute, matchCpdTeamRoute],
        loadComponent: () => import('./pages/cpdteam/cpdteam').then((m) => m.CpdTeam),
      },
      {
        path: 'cpd/team-cpd/:userId',
        canMatch: [matchCpdModuleRoute, matchCpdTeamRoute],
        loadComponent: () => import('./pages/cpdteam/cpdteam').then((m) => m.CpdTeam),
      },
      {
        path: 'cpd/team-cpd',
        canMatch: [matchCpdModuleRoute, matchCpdTeamRoute],
        loadComponent: () => import('./pages/cpdteam/cpdteam').then((m) => m.CpdTeam),
      },
      {
        path: 'cpd/view/:id',
        canMatch: [matchCpdModuleRoute, matchCpdMyRoute],
        loadComponent: () => import('./pages/cpd/cpd').then((m) => m.Cpd),
      },
      {
        path: 'cpd',
        canMatch: [matchCpdModuleRoute, matchCpdMyRoute],
        loadComponent: () => import('./pages/cpd/cpd').then((m) => m.Cpd),
      },
      {
        path: 'exam-zone',
        data: {
          title: 'Exam Zone',
          description: 'Access exam resources and progress checkpoints.',
        },
        loadChildren: () =>
          import('./pages/exam-zone/exam-zone.routes').then((m) => m.examZoneRoutes),
      },
      {
        path: 'my-commission',
        canMatch: [matchMyCommissionsRoute],
        data: {
          title: 'My Commission',
          description: 'Review your commission statements and payouts.',
        },
        loadComponent: () =>
          import('./pages/my-commissions/my-commissions').then((m) => m.MyCommissions),
      },
      {
        path: 'documents-library',
        data: {
          title: 'Documents Library',
          description: 'Browse and manage shared CRM documents.',
        },
        loadComponent: () =>
          import('./pages/documents-library/documents-library.component').then(
            (m) => m.DocumentsLibraryComponent,
          ),
      },
      {
        path: 'reports',
        data: {
          title: 'Reports',
          description: 'Generate and review CRM performance reports.',
        },
        loadComponent: () => import('./pages/reports/reports').then((m) => m.Reports),
      },
      {
        path: 'configurations',
        loadChildren: () =>
          import('./pages/configurations/configurations.routes').then(
            (m) => m.configurationsRoutes,
          ),
      },
      {
        path: 'support',
        data: {
          title: 'Support',
          description: 'Find support guides and assistance resources.',
        },
        loadComponent: () =>
          import('./pages/support/support.component').then((m) => m.SupportComponent),
      },
      {
        path: 'help-center',
        data: {
          title: 'Help Centre',
          description: 'Find guides, tutorials and answers to get the most out of the platform.',
        },
        loadChildren: () =>
          import('./pages/help-center/help-center.routes').then((m) => m.helpCenterRoutes),
      },
      {
        path: 'contact-support',
        data: {
          title: 'Contact Support',
          description: 'Get in touch with our support team via chat, phone, or email.',
        },
        loadChildren: () =>
          import('./pages/contact-support/contact-support.routes').then((m) => m.contactSupportRoutes),
      },
      {
        path: 'suitability-templates',
        data: {
          title: 'Suitability Templates',
          description: 'Create and manage reusable suitability report templates.',
        },
        loadChildren: () =>
          import('./pages/suitability-templates/suitability-templates.routes').then(
            (m) => m.suitabilityTemplatesRoutes,
          ),
      },
      {
        path: 'notifications',
        data: {
          title: 'Notifications',
          description: 'Review all CRM notifications and platform updates.',
        },
        loadComponent: () =>
          import('./features/notification/crm-notification').then(
            (m) => m.CrmNotificationComponent,
          ),
      },
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'dashboard',
      },
    ],
  },
  {
    path: '**',
    canMatch: [matchUnknownRoute],
    loadComponent: () =>
      import('../../../../libs/core/shared/ui/error-reusable/error-reusable').then(
        (m) => m.ErrorReusableComponent,
      ),
  },
];
